import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentMeta from 'react-document-meta';
import { IoChevronForward, IoHelpCircleOutline } from 'react-icons/io5';
import PersonalityHeader from './components/Header';
import SideCharacters from './components/SideCharacters';
import { characterInfo, findCharacter, questionPlaceholderImage, typeGroupColor } from './data/characters';
import { loadDiagnosisResult } from './data/storage';
import { PersonalityDiagnosisResult } from './data/scoring';
import Styles from './styles.module.css';

const PersonalityHome = () => {
  const navigate = useNavigate();
  const [savedResult, setSavedResult] = useState<PersonalityDiagnosisResult | null>(null);

  useEffect(() => {
    setSavedResult(loadDiagnosisResult());
  }, []);

  const meta = {
    title: '性格診断 | memorii works',
    description: '32の質問に答えるだけで、あなたの美容師像と価値観を診断。相性の良い美容室タイプも分かります。誰でも無料・登録なしで受けられます。',
    canonical: 'https://memorii-works.com/personality',
    meta: { charset: 'utf-8' },
  };

  const matched = savedResult ? findCharacter(savedResult.type) : undefined;
  const hasResult = Boolean(savedResult && matched);

  const openOwnResult = () => {
    if (!savedResult) return;
    navigate(`/personality/result/${savedResult.type}`, {
      state: {
        introduce: false,
        results: savedResult.results,
        rawScores: savedResult.rawScores,
        axisStates: savedResult.axisStates,
      },
    });
  };

  const openIntroduce = (type: string) => {
    navigate(`/personality/result/${type}`, { state: { introduce: true } });
  };

  return (
    <DocumentMeta {...meta}>
      <PersonalityHeader title="性格診断" mode="close" />
      <SideCharacters imagePath={matched ? matched.imagePath : questionPlaceholderImage} />
      <div className={Styles.page}>
        <div className={Styles.heroCard}>
          <div className={Styles.heroIcon}>
            <IoHelpCircleOutline size={26} />
          </div>
          <h1 className={Styles.heroTitle}>あなたの美容師像を診断</h1>
          <p className={Styles.heroDesc}>
            将来の美容師像を診断するだけでなく、
            <br />
            あなたの価値観と美容室のマッチ率を診断します。
          </p>
          <button type="button" className={Styles.startButton} onClick={() => navigate('/personality/question')}>
            診断を開始する
          </button>
          <p className={Styles.heroNote}>登録不要・約3分・誰でも無料で受けられます</p>
        </div>

        <div className={Styles.resultSection}>
          <div className={Styles.sectionLabel}>診断結果</div>
          {hasResult && matched ? (
            <button type="button" className={Styles.resultCard} onClick={openOwnResult}>
              <img src={matched.heroPath} alt={matched.subtitle} className={Styles.resultImage} />
              <div className={Styles.resultOverlay}>
                <span className={Styles.resultBadge} style={{ backgroundColor: typeGroupColor(matched.title) }}>
                  {matched.title}
                </span>
                <p className={Styles.resultSubtitle}>{matched.subtitle}</p>
                {matched.catchphrase && <p className={Styles.resultCatch}>{matched.catchphrase}</p>}
              </div>
            </button>
          ) : (
            <div className={Styles.emptyResult}>
              <img src={questionPlaceholderImage} alt="未診断" className={Styles.emptyImage} />
              <p className={Styles.emptyText}>未診断</p>
            </div>
          )}
        </div>

        <div className={Styles.listSection}>
          <div className={Styles.sectionLabel}>キャラクター一覧</div>
          <div className={Styles.list}>
            {characterInfo.map((c) => {
              const groupColor = typeGroupColor(c.title);
              return (
                <button type="button" key={c.title} className={Styles.listRow} onClick={() => openIntroduce(c.title)}>
                  <div className={Styles.listThumbWrap} style={{ backgroundColor: `${groupColor}59` }}>
                    <img src={c.imagePath} alt={c.title} className={Styles.listThumb} />
                  </div>
                  <div className={Styles.listBody}>
                    <span className={Styles.listBadge} style={{ backgroundColor: groupColor }}>{c.title}</span>
                    <p className={Styles.listSubtitle}>{c.subtitle}</p>
                    <p className={Styles.listCatch}>{c.catchphrase}</p>
                  </div>
                  <IoChevronForward size={18} color="#BBBBBB" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </DocumentMeta>
  );
};

export default PersonalityHome;
